import React, { useMemo, useState } from "react";
import { Label, Select, Textarea, TextInput } from "flowbite-react";
import { Surface } from "../../../../atoms/Surface";
import { AlertMessage } from "../../../../atoms/AlertMessage";
import { Controller, useWatch } from "react-hook-form";
import type { Control, FieldErrors, UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HiInformationCircle } from "react-icons/hi";
import {
  MdOutlineDraw,
  MdOutlineDescription,
  MdArrowForward,
} from "react-icons/md";

import NoTranslate from "../../../../atoms/NoTranslate";
import useAuth from "../../../../../hooks/useAuth";
import { PlanTier, usePermissions } from "../../../../../utils/permissions";
import { Cap, Level } from "../../../../../utils/rbacCatalog";
import FeatureUpgradePrompt from "../../../../atoms/FeatureUpgradePrompt";
import { PlansModal } from "../../../../modals/PlansModal";
import { useGetESignatureTemplates } from "../../../../../hooks/esignature";
import { CustomButton } from "../../../../atoms/CustomButton";
import { EmptyState } from "../../../../atoms/EmptyState";
import { Switch } from "../../../../atoms/Switch";
import type { RegistrationFormData } from "../types";

type Props = {
  control: Control<RegistrationFormData>;
  errors: FieldErrors<RegistrationFormData>;
  setValue?: UseFormSetValue<RegistrationFormData>;
  readOnly?: boolean;
};

const MIN_EXPIRY_DAYS = 1;
const MAX_EXPIRY_DAYS = 180;
const DEFAULT_EXPIRY_DAYS = 30;
const MAX_MESSAGE_LENGTH = 300;

export const EsignatureSection: React.FC<Props> = ({
  control,
  errors,
  setValue,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, customer_identification } = useAuth();
  const permissions = usePermissions(user);
  const entityIdentification = user?.entity_identification || "";

  const hasEsignatureAccess = permissions.hasFeatureAccess("ESIGNATURE_ACCESS");
  const canReadEsignatureConfig = permissions.can(
    Cap.ESIGNATURE_CONFIG,
    Level.VIEW
  );

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const { data: templates, isLoading: templatesLoading } =
    useGetESignatureTemplates(
      customer_identification,
      entityIdentification,
      !hasEsignatureAccess || !canReadEsignatureConfig
    );

  const esignatureEnabled = useWatch({ control, name: "esignature.enabled" });
  const messageValue: string =
    useWatch({ control, name: "esignature.message" }) || "";
  const expiryDays = useWatch({ control, name: "esignature.expiry_days" });

  const hasTemplates = !!templates && templates.length > 0;

  const benefits = useMemo(
    () => [
      {
        id: "benefit-1",
        text: t("customRegistration.esignature.upgrade.benefit1") as string,
      },
      {
        id: "benefit-2",
        text: t("customRegistration.esignature.upgrade.benefit2") as string,
      },
      {
        id: "benefit-3",
        text: t("customRegistration.esignature.upgrade.benefit3") as string,
      },
    ],
    [t]
  );

  const handleOpenTemplatesPage = () => {
    navigate("/control-panel/e-signature");
  };

  return (
    <Surface>
      <div className="mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <MdOutlineDraw size={22} className="text-green-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-gray-900 text-base font-semibold mb-1">
              {t("customRegistration.esignature.title")}
            </h3>
            <p className="text-gray-500 text-sm leading-tight">
              {t("customRegistration.esignature.description")}
            </p>
          </div>
        </div>
      </div>

      {!hasEsignatureAccess ? (
        <>
          <FeatureUpgradePrompt
            title={t("customRegistration.esignature.upgrade.title") as string}
            description={
              t("customRegistration.esignature.upgrade.description") as string
            }
            benefitObjects={benefits}
            icon={MdOutlineDraw}
            iconTheme="green"
            upgradeText={t("dashboard.upgrade.key3") as string}
            onUpgrade={() => setShowUpgradeModal(true)}
          />
          <PlansModal
            showPlansModal={showUpgradeModal}
            setShowPlansModal={setShowUpgradeModal}
            targetPlan={PlanTier.ENTERPRISE}
          />
        </>
      ) : (
        <>
          <div className="mb-4">
            <Controller
              name="esignature.enabled"
              control={control}
              render={({ field }) => (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <Label htmlFor="esignature-toggle" className="cursor-pointer">
                    <span className="text-sm font-medium text-gray-900">
                      {t("customRegistration.esignature.enableLabel")}
                    </span>
                  </Label>
                  <Switch
                    id="esignature-toggle"
                    checked={field.value || false}
                    disabled={templatesLoading || !hasTemplates}
                    onChange={(next) => {
                      field.onChange(next);
                      if (
                        next &&
                        (expiryDays === undefined ||
                          expiryDays === null ||
                          expiryDays === 0)
                      ) {
                        setValue?.(
                          "esignature.expiry_days",
                          DEFAULT_EXPIRY_DAYS
                        );
                      }
                    }}
                  />
                </div>
              )}
            />
          </div>

          {canReadEsignatureConfig && !templatesLoading && !hasTemplates && (
            <EmptyState
              bordered
              icon={<MdOutlineDescription size={24} />}
              title={t("customRegistration.esignature.emptyTemplates.title")}
              description={t(
                "customRegistration.esignature.emptyTemplates.description"
              )}
              action={
                <CustomButton
                  variant="outline"
                  onClick={handleOpenTemplatesPage}
                  title={
                    t(
                      "customRegistration.esignature.emptyTemplates.cta"
                    ) as string
                  }
                  icon="plus"
                />
              }
            />
          )}

          {esignatureEnabled && hasTemplates && (
            <div className="space-y-4 animate-slideIn">
              <section className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                  <div>
                    <h4 className="flex items-center text-base font-semibold text-gray-900">
                      <MdOutlineDescription
                        className="text-gray-400 flex-shrink-0"
                        size={18}
                      />
                      <span className="ml-2">
                        {t("customRegistration.esignature.template.label")}
                      </span>
                    </h4>
                    <p className="mt-1 text-xs text-gray-600">
                      {t("customRegistration.esignature.template.helper")}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleOpenTemplatesPage}
                    className="self-start sm:self-auto inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-800 font-medium whitespace-nowrap"
                    title={
                      t(
                        "customRegistration.esignature.template.manage"
                      ) as string
                    }
                  >
                    <MdArrowForward size={14} />
                    {t("customRegistration.esignature.template.manage")}
                  </button>
                </div>

                <NoTranslate>
                  <Controller
                    name="esignature.template_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        value={field.value || ""}
                        className="w-full"
                      >
                        <option value="">
                          {t(
                            "customRegistration.esignature.template.placeholder"
                          )}
                        </option>
                        {(templates || []).map((tpl) => (
                          <option key={tpl.template_id} value={tpl.template_id}>
                            {tpl.name}
                          </option>
                        ))}
                      </Select>
                    )}
                  />
                </NoTranslate>

                {errors?.esignature?.template_id && (
                  <p className="mt-2 text-sm font-medium text-red-600">
                    {errors.esignature.template_id.message}
                  </p>
                )}
              </section>

              <section className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="mb-3">
                  <h4 className="flex flex-col items-start sm:flex-row sm:items-center text-base font-semibold text-gray-900">
                    <span>
                      {t("customRegistration.esignature.message.label")}
                    </span>
                    <span className="sm:ml-2 text-sm font-normal text-gray-500">
                      {t("dashboard.entities.viewEntityUser.sendMsg.optional")}
                    </span>
                  </h4>
                  <p className="mt-1 text-xs text-gray-600">
                    {t("customRegistration.esignature.message.helper")}
                  </p>
                </div>
                <NoTranslate>
                  <Controller
                    name="esignature.message"
                    control={control}
                    render={({ field }) => (
                      <Textarea
                        {...field}
                        value={field.value || ""}
                        rows={3}
                        maxLength={MAX_MESSAGE_LENGTH}
                        placeholder={
                          t(
                            "customRegistration.esignature.message.placeholder"
                          ) as string
                        }
                        className="w-full resize-y"
                      />
                    )}
                  />
                </NoTranslate>
                {errors?.esignature?.message &&
                  typeof errors.esignature.message === "object" && (
                    <p className="mt-2 text-sm font-medium text-red-600">
                      {errors.esignature.message.message}
                    </p>
                  )}
                <div className="mt-1 flex justify-end text-xs text-gray-400">
                  {messageValue.length}/{MAX_MESSAGE_LENGTH}
                </div>
              </section>

              <section className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="mb-3">
                  <h4 className="text-base font-semibold text-gray-900">
                    {t("customRegistration.esignature.expiry.label")}
                  </h4>
                  <p className="mt-1 text-xs text-gray-600">
                    {t("customRegistration.esignature.expiry.helper", {
                      min: MIN_EXPIRY_DAYS,
                      max: MAX_EXPIRY_DAYS,
                    })}
                  </p>
                </div>
                <NoTranslate>
                  <Controller
                    name="esignature.expiry_days"
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        type="number"
                        min={MIN_EXPIRY_DAYS}
                        max={MAX_EXPIRY_DAYS}
                        value={field.value ?? DEFAULT_EXPIRY_DAYS}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          if (Number.isNaN(v)) {
                            field.onChange("");
                          } else {
                            field.onChange(
                              Math.min(
                                Math.max(v, MIN_EXPIRY_DAYS),
                                MAX_EXPIRY_DAYS
                              )
                            );
                          }
                        }}
                        className="w-32"
                      />
                    )}
                  />
                </NoTranslate>
                {errors?.esignature?.expiry_days && (
                  <p className="mt-2 text-sm font-medium text-red-600">
                    {errors.esignature.expiry_days.message}
                  </p>
                )}
              </section>
            </div>
          )}

          {errors?.esignature?.root && (
            <AlertMessage
              color="failure"
              icon={HiInformationCircle}
              className="mt-2"
            >
              {errors.esignature.root.message}
            </AlertMessage>
          )}
        </>
      )}
    </Surface>
  );
};

export default EsignatureSection;
